import { Controller } from '@hotwired/stimulus';
import EmblaCarousel from 'embla-carousel';
import { disablePrevNextBtns } from './carousel/prevAndNextButtons';
import { setupDotBtns, generateDotBtns, selectDotBtn } from './carousel/dotButtons';
import { useDispatch, useWindowResize } from 'stimulus-use';

export default class embla extends Controller {
	static targets = ['viewport', 'prevButton', 'nextButton', 'dots'];
	static values = { settings: Object };
	carousel = null;
	inertRaf = null;
	intersectionObserver = null;
	visibleSlideIdxs = new Set();
	focusableSelector =
		'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

	connect() {
		useWindowResize(this);
		this.initializeCarousel();
		this.observeVisibility();
	}

	disconnect() {
		this.visibilityObserver?.disconnect?.();
		this.visibilityObserver = null;
	}

	windowResize() {
		useDispatch(this);
		clearTimeout(this.resizeTimeout);
		this.resizeTimeout = setTimeout(() => {
			this.initializeCarousel();
			this.carousel.reInit();
		}, 150);
	}

	// The carousel can be initialized while hidden (e.g. inside an inactive tab panel or a closed
	// accordion), where every measurement is 0 and the edge-state (is-start/is-end gradient) is
	// wrong. Re-measure once the element gains width.
	observeVisibility() {
		if (typeof ResizeObserver === 'undefined') return;
		this.lastObservedWidth = this.element.offsetWidth;
		this.visibilityObserver = new ResizeObserver(() => {
			const width = this.element.offsetWidth;
			if (width === this.lastObservedWidth) return;
			this.lastObservedWidth = width;
			if (width > 0) this.carousel?.reInit();
		});
		this.visibilityObserver.observe(this.element);
	}

	initializeCarousel() {
		this.optionsDefault = {
			loop: false,
			align: 'start',
			containScroll: 'trimSnaps',
			skipSnaps: true,
			speed: 20,
			slidesToScroll: 'auto',
			watchFocus: false,
			target: document.querySelector('.embla__viewport'),
			...this.settingsValue,
		};

		// A single-slide carousel must not be swipeable/draggable.
		const slideCount = this.viewportTarget?.querySelector('.embla__container')?.children.length ?? 0;
		if (slideCount <= 1) this.optionsDefault.watchDrag = false;

		this.element.classList.add('is-initialized');
		this.intersectionObserver?.disconnect?.();
		this.intersectionObserver = null;
		this.visibleSlideIdxs = new Set();
		this.carousel = EmblaCarousel(this.viewportTarget, this.optionsDefault);
		this.slides = this.carousel.slideNodes();
		this.toggleSingleSlideNavigation();

		const updateEdgeState = () => {
			if (!this.carousel) return;
			const canPrev = this.carousel.canScrollPrev?.() ?? false;
			const canNext = this.carousel.canScrollNext?.() ?? false;

			if (!canPrev && !canNext) {
				this.element.classList.add('is-start', 'is-end');
				return;
			}

			const progress = this.carousel.scrollProgress?.();
			const epsilon = 0.001;
			const hasProgress = Number.isFinite(progress);
			const isAtStart = hasProgress ? progress <= epsilon : !canPrev;
			const isAtEnd = hasProgress ? progress >= 1 - epsilon : !canNext;

			this.element.classList.toggle('is-start', isAtStart);
			this.element.classList.toggle('is-end', isAtEnd);
		};

		this.carousel.on('init', updateEdgeState);
		this.carousel.on('reInit', updateEdgeState);
		this.carousel.on('select', updateEdgeState);
		this.carousel.on('scroll', updateEdgeState);
		this.carousel.on('settle', updateEdgeState);
		requestAnimationFrame(updateEdgeState);

		this.setupVisibilityObserver();
		this.setupButtonsAndDots();
	}

	toggleSingleSlideNavigation() {
		const hasSingleSlide = this.slides?.length <= 1;
		if (!hasSingleSlide) return;

		const navigationTargets = [
			this.hasPrevButtonTarget ? this.prevButtonTarget : null,
			this.hasNextButtonTarget ? this.nextButtonTarget : null,
			this.hasDotsTarget ? this.dotsTarget : null,
		].filter(Boolean);
		const controlGroups = new Set(navigationTargets.map((target) => target.closest('[aria-label="Carousel controls"]')).filter(Boolean));

		controlGroups.forEach((group) => group.remove());
		navigationTargets.forEach((target) => {
			if (!target.isConnected) return;
			target.remove();
		});
	}

	setupButtonsAndDots() {
		if (this.hasPrevButtonTarget && this.hasNextButtonTarget) {
			const disablePrevAndNextBtns = disablePrevNextBtns(this.prevButtonTarget, this.nextButtonTarget, this.carousel);
			this.carousel.on('select', disablePrevAndNextBtns);
			this.carousel.on('init', disablePrevAndNextBtns);
			this.carousel.on('reInit', disablePrevAndNextBtns);

			[this.prevButtonTarget, this.nextButtonTarget].forEach((button) => {
				button.addEventListener('focusin', () => {
					this.viewportTarget.setAttribute('aria-live', 'polite');
				});
				button.addEventListener('focusout', () => {
					this.viewportTarget.setAttribute('aria-live', 'off');
				});
			});
		}

		if (this.hasDotsTarget) {
			let dotsArray = [];
			let setSelectedDotBtn = () => {};
			const buildDots = () => {
				dotsArray = generateDotBtns(this.dotsTarget, this.carousel);
				setSelectedDotBtn = selectDotBtn(dotsArray, this.carousel);
				if (dotsArray.length) {
					setupDotBtns(dotsArray, this.carousel);
					setSelectedDotBtn();
				}
			};

			this.carousel.on('init', buildDots);
			this.carousel.on('reInit', buildDots);
			requestAnimationFrame(buildDots);

			this.carousel.on('select', () => setSelectedDotBtn());

			const addDotListeners = (dot) => {
				dot.addEventListener('focusin', (e) => {
					if (e.target.matches(':focus-visible')) {
						this.dotsTarget.classList.add('is-focused');
						this.viewportTarget.setAttribute('aria-live', 'polite');
					}
				});

				dot.addEventListener('focusout', () => {
					this.dotsTarget.classList.remove('is-focused');
					this.viewportTarget.setAttribute('aria-live', 'off');
				});

				dot.addEventListener('keydown', (e) => {
					if (e.key === 'ArrowRight' && this.carousel.canScrollNext()) {
						e.preventDefault();
						this.carousel.scrollNext();
						dotsArray[this.carousel.selectedScrollSnap()]?.focus();
					}
					if (e.key === 'ArrowLeft' && this.carousel.canScrollPrev()) {
						e.preventDefault();
						this.carousel.scrollPrev();
						dotsArray[this.carousel.selectedScrollSnap()]?.focus();
					}
					if (e.key === 'Tab' && !e.shiftKey) {
						e.preventDefault();
						this.dotsTarget.classList.remove('is-focused');
						this.viewportTarget.setAttribute('aria-live', 'off');
						this.focusFirstInView();
						return;
					}
					if (e.key === 'Tab') {
						this.dotsTarget.classList.remove('is-focused');
					}
				});
			};

			this.dotsTarget.addEventListener('focusin', (e) => {
				if (e.target.matches(':focus-visible')) {
					dotsArray[this.carousel.selectedScrollSnap()]?.focus();
				}
			});

			const bindDotListeners = () => dotsArray.forEach(addDotListeners);
			this.carousel.on('init', bindDotListeners);
			this.carousel.on('reInit', bindDotListeners);
			requestAnimationFrame(bindDotListeners);
		}

		const updateTabindex = this.updateTabindex.bind(this);
		this.carousel.on('select', updateTabindex);
		this.carousel.on('init', updateTabindex);
		updateTabindex();

		const scheduleInertUpdate = this.scheduleInertUpdate.bind(this);
		this.carousel.on('init', () => requestAnimationFrame(scheduleInertUpdate));
		this.carousel.on('reInit', () => requestAnimationFrame(scheduleInertUpdate));
		this.carousel.on('slidesInView', scheduleInertUpdate);
		this.carousel.on('settle', scheduleInertUpdate);
		this.carousel.on('select', scheduleInertUpdate);
		requestAnimationFrame(scheduleInertUpdate);
	}

	setupVisibilityObserver() {
		if (!this.viewportTarget || !this.slides?.length) return;

		// Rooted in the viewport so we measure what's actually visible on screen.
		// Use a high threshold + bounding-rect fallback to treat "in viewport" as "fully visible".
		const thresholds = [0, 0.5, 0.75, 0.9, 0.95, 0.98, 1];
		const idxByEl = new Map();
		this.slides.forEach((slide, idx) => idxByEl.set(slide, idx));

		const isFullyInside = (slideEl) => {
			const vr = this.viewportTarget.getBoundingClientRect();
			const sr = slideEl.getBoundingClientRect();
			const eps = 1; // px tolerance for subpixel rounding
			return sr.left >= vr.left - eps && sr.right <= vr.right + eps && sr.top >= vr.top - eps && sr.bottom <= vr.bottom + eps;
		};

		this.intersectionObserver = new IntersectionObserver(
			(entries) => {
				let changed = false;
				entries.forEach((entry) => {
					const idx = idxByEl.get(entry.target);
					if (idx === undefined) return;
					const inView =
						(entry.isIntersecting && entry.intersectionRatio >= 0.98) || (entry.isIntersecting && isFullyInside(entry.target));

					if (inView) {
						if (!this.visibleSlideIdxs.has(idx)) {
							this.visibleSlideIdxs.add(idx);
							changed = true;
						}
					} else {
						if (this.visibleSlideIdxs.delete(idx)) {
							changed = true;
						}
					}
				});
				if (changed) this.scheduleInertUpdate();
			},
			{ root: this.viewportTarget, threshold: thresholds },
		);

		this.slides.forEach((slide) => this.intersectionObserver.observe(slide));
		// Prime once in case the observer callback hasn't fired yet.
		requestAnimationFrame(() => this.scheduleInertUpdate());
	}

	getSlidesInView() {
		if (this.visibleSlideIdxs?.size) return Array.from(this.visibleSlideIdxs);

		const fn = this.carousel?.slidesInView;
		if (!fn) return [];
		// Prefer "in view" indices that are fully in view if the API supports it.
		try {
			return fn.call(this.carousel, true) ?? [];
		} catch (_) {
			return fn.call(this.carousel) ?? [];
		}
	}

	scheduleInertUpdate() {
		if (this.inertRaf) return;
		this.inertRaf = requestAnimationFrame(() => {
			this.inertRaf = null;
			this.updateSlidesInert();
		});
	}

	setSlideFocusable(slide, enabled) {
		const nodes = slide.querySelectorAll(this.focusableSelector);
		nodes.forEach((el) => {
			if (enabled) {
				if (el.dataset.origTabindex !== undefined) {
					if (el.dataset.origTabindex === '') el.removeAttribute('tabindex');
					else el.setAttribute('tabindex', el.dataset.origTabindex);
					delete el.dataset.origTabindex;
				}
			} else {
				if (el.dataset.origTabindex === undefined) {
					el.dataset.origTabindex = el.getAttribute('tabindex') ?? '';
				}
				el.setAttribute('tabindex', '-1');
			}
		});
	}

	updateSlidesInert() {
		if (!this.slides?.length) return;

		const idxs = this.getSlidesInView();
		const safeIdxs = idxs.length ? idxs : [this.carousel.selectedScrollSnap()];
		const inView = new Set(safeIdxs);

		this.slides.forEach((slide, i) => {
			const enabled = inView.has(i);
			slide.toggleAttribute('inert', !enabled);
			slide.setAttribute('aria-hidden', enabled ? 'false' : 'true');
			// Fallback for cases where `inert` isn't fully respected by the browser/CSS.
			this.setSlideFocusable(slide, enabled);
		});
	}

	focusFirstInView() {
		if (!this.slides?.length) return;
		const idxs = this.getSlidesInView();
		const safeIdxs = idxs.length ? idxs : [this.carousel.selectedScrollSnap()];
		const firstIdx = Math.min(...safeIdxs);
		const slide = this.slides[firstIdx];
		if (!slide) return;

		this.updateSlidesInert();

		const focusable = slide.querySelector(this.focusableSelector);
		focusable?.focus?.({ preventScroll: true });
	}

	updateTabindex() {
		if (!this.slides || this.slides.length === 0) return;

		const selectedIndex = this.carousel.selectedScrollSnap();

		this.slides.forEach((slide, index) => {
			const button = slide.querySelector('button[data-gallery-index]');
			if (button) {
				button.setAttribute('tabindex', index === selectedIndex ? '0' : '-1');
			}
		});
	}

	prev(event) {
		event.preventDefault();
		this.carousel.scrollPrev();
	}

	next(event) {
		event.preventDefault();
		this.carousel.scrollNext();
	}

	goTo(event) {
		event.detail.context.forEach((context) => {
			var targetCarousel = context.querySelector('.embla');
			if (targetCarousel && this.element === targetCarousel) {
				this.carousel.scrollTo(event.detail.key);
			}
		});
	}

	update(event) {
		event.detail.context.forEach((context) => {
			var targetCarousel = context.querySelector('.embla');
			if (targetCarousel && this.element === targetCarousel) {
				this.initializeCarousel();
				if (this.carousel.canScrollPrev()) this.prevButtonTarget.removeAttribute('disabled');
				if (this.carousel.canScrollNext()) this.nextButtonTarget.removeAttribute('disabled');
			}
		});
	}

	setActiveSlides() {
		this.slides.forEach((item) => item.classList.remove('is-selected'));
		if (this.element.classList.contains('embla--fade')) {
			this.slides[this.carousel.selectedScrollSnap()].classList.add('is-selected');
		} else {
			this.carousel.slidesInView().forEach((index) => this.slides[index].classList.add('is-selected'));
		}
	}

	runNecessary = () => {
		this.setActiveSlides();
	};

	checkActiveSlide(e) {
		e.detail.context.forEach((context) => {
			var targetCarousel = context.querySelector('.embla');
			if (targetCarousel && this.element === targetCarousel) {
				var activeSlides = this.carousel.slidesInView();
				if (e.detail.key > activeSlides[activeSlides.length - 1]) {
					this.carousel.scrollTo(this.carousel.selectedScrollSnap() + 1);
				} else if (e.detail.key < activeSlides[0]) {
					this.carousel.scrollTo(this.carousel.selectedScrollSnap() - 1);
				}
			}
		});
	}
}
